import { EntryRow, useDayEntries } from "./Journal";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { MoodFace } from "../../components/ui";
import { EmptyState } from "../../components/ui/ds";
import { Glyph } from "../../components/ui";
import { iconChipStyle } from "../../lib/icons-resolve";
import { MOODS, moodIconName } from "../../lib/constants";
import { WEEKDAYS_SK, fmtMetric, todayKey } from "../../lib/format";
import "./activity-heatmap.css";

// ─────────────────────────────────────────────────────────────
//  Heat mapa „Aktivita" (PRO / non-Lite) — podľa mockupu majiteľky.
//  Widget na tabuľkovom prehľade = Úspešnosť (badge % + intenzita buniek);
//  modal = default Body s prepínačom na Úspešnosť, mriežka mesiace/týždne.
//  v29 — guličky = série (plná = aktuálna séria, obrys = najdlhšia séria),
//  v modáli prepínač nálad do štvorčekov + poznámka k nálade v detaile dňa.
//  v37 — ceny z kolesa výhry v mriežke: VOĽNÝ DEŇ = prerušovaný rámik bunky,
//  DVOJNÁSOBNÝ DEŇ = značka „×2" v ľavom hornom rohu (legenda + detail dňa ich
//  pomenujú slovom; naplánovaná budúca cena má riadok „Čaká ťa" nad mriežkou).
//  Dáta: GET /api/activity?from=&to= (read-only) + freeDays/doubleDays z /api/state.
//  Farby výhradne color-mix z --brand-primary → funguje vo všetkých témach.
// ─────────────────────────────────────────────────────────────

function dkey(d) {
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
}
function shiftKey(k, n) {
  const d = new Date(k + "T00:00:00");
  d.setDate(d.getDate() + n);
  return dkey(d);
}
function mondayOf(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
}
// v29 — kalendárny rozsah série: posledných `len` štvorčekov končiacich `endKey`
// (aktuálna séria končí dnes, ak dnes už bola aktivita, inak včera; najdlhšia
// končí dňom bestStreakEnd zo servera). Zjednodušený model podľa majiteľky:
// „ak bol používateľ aktívny posledných 7 dní, posledných 7 štvorcov má guličku".
function rangeOf(len, endKey) {
  if (!len || len <= 0 || !endKey) return null;
  return { from: shiftKey(endKey, -(len - 1)), to: endKey };
}
function streakRanges({ streak, streakActive, best, bestEnd }) {
  const cur = rangeOf(streak, streakActive ? todayKey() : shiftKey(todayKey(), -1));
  return { cur, best: rangeOf(best, bestEnd) };
}
const inRange = (day, r) => !!(r && day >= r.from && day <= r.to);

// rozsah ukotvený na pondelky → vždy presne `weeksBack` riadkov mriežky (posledný
// týždeň doplnený null-ami za budúce dni), nezávisle od aktuálneho dňa v týždni
async function loadActivity(weeksBack) {
  const to = new Date();
  const from = mondayOf(to);
  from.setDate(from.getDate() - (weeksBack - 1) * 7);
  const r = await api(`/api/activity?from=${dkey(from)}&to=${dkey(to)}`);
  return r.days || [];
}
// dni → riadky Po–Ne; posledný (aktuálny) týždeň doplnený null-ami za budúce dni
function toWeeks(days) {
  if (!days.length) return [];
  const weeks = [];
  let row = [];
  const first = new Date(days[0].day + "T00:00:00");
  for (let i = (first.getDay() + 6) % 7; i > 0; i--) row.push(null);
  for (const d of days) {
    row.push(d);
    if (row.length === 7) { weeks.push(row); row = []; }
  }
  if (row.length) { while (row.length < 7) row.push(null); weeks.push(row); }
  return weeks;
}
const isEmptyDay = (d) => !d || (d.due === 0 && d.done === 0 && d.points === 0);

// intenzita bunky: v = 0..100 → prímes brand farby (prázdny deň = neutrálny podklad)
function cellBg(v) {
  if (!v || v <= 0) return "var(--neutral-surface-3)";
  const mix = Math.round(14 + Math.min(100, v) * 0.7);
  return `color-mix(in srgb, var(--brand-primary) ${mix}%, var(--neutral-surface))`;
}
// v30 — JEDNA kombinovaná metrika: 50 % úspešnosť + 50 % body (normalizované voči
// najlepšiemu dňu v rozsahu). Deň so splneným plánom svieti, bodovo silný deň tiež,
// a doplnené dni s 0 bodmi nezmiznú; presné čísla ukáže detail dňa.
const cellValue = (d, maxPts) => (!d ? 0 : Math.round(0.5 * (d.pct || 0) + 50 * (maxPts ? d.points / maxPts : 0)));

// v30 — séria už NIE je gulička v strede (stred patrí nálade): pásik pri spodnej
// hrane štvorčeka. Plný široký = aktuálna séria, kratší polopriehľadný = najdlhšia.
function streakStripe(kind, v) {
  const c = v >= 40 ? "var(--brand-on-primary)" : "var(--brand-primary)";
  const cur = kind === "cur";
  return <span aria-hidden="true" style={{ position: "absolute", bottom: 2.5, left: cur ? "16%" : "30%", right: cur ? "16%" : "30%", height: 2.5, borderRadius: "var(--radius-pill)", background: c, opacity: cur ? 0.95 : 0.5 }} />;
}

// v33i (task #5) — Lite bunka: intenzita = ČISTO % úspešnosti (bez bodov), zobrazuje len náladu.
// Žiadny „perfektný deň" krúžok ani sériová stopa (to boli PRO-only „odznakové" detaily).
function DayCell({ d, maxPts, selected, onSelect, ranges, mood, lite = false, free = false, dbl = false }) {
  const today = d && d.day === todayKey();
  const v = lite ? (d ? (d.pct || 0) : 0) : cellValue(d, maxPts);
  // značky v bunke (perfektný deň, cena z kolesa) sa na tmavom podklade preklápajú do
  // kontrastnej farby — jedno pravidlo pre všetky, aby sa nemohli rozísť
  const mark = v >= 40 ? "var(--brand-on-primary)" : "var(--brand-primary)";
  // v33h — séria aj perfektný deň sa značia AJ v Lite (odznaky série boli v Lite chybne skryté)
  const stripeKind = d && (inRange(d.day, ranges && ranges.cur) ? "cur" : (inRange(d.day, ranges && ranges.best) ? "best" : null));
  const style = {
    position: "relative", aspectRatio: "1", width: "auto", minWidth: 0, padding: 0, cursor: d && onSelect ? "pointer" : "default",
    borderRadius: "var(--radius-sm)", background: d ? cellBg(v) : "transparent",
    outline: selected ? "2px solid var(--brand-primary)" : (today ? "1.5px solid var(--brand-primary-border)" : "none"),
    outlineOffset: 1.5, display: "flex", alignItems: "center", justifyContent: "center",
    "--heat-mark": mark,
  };
  // nálada je v štvorčeku VŽDY (stred), séria beží ako pásik pri spodnej hrane
  // v32.16 — perfektný deň (všetko naplánované splnené) = krúžok v pravom hornom rohu (PRO only)
  const perfect = d && (free || (d.due > 0 && (d.pct || 0) >= 100));
  const inner = (<>
    {mood ? <MoodFace k={moodIconName(mood)} size={14} color={v >= 40 ? "var(--brand-on-primary)" : "var(--text)"} /> : null}
    {perfect && <span aria-hidden="true" style={{ position: "absolute", top: 2.5, right: 2.5, width: 7, height: 7, borderRadius: "50%", boxSizing: "border-box", border: "1.6px solid " + mark }} />}
    {/* v37 — cena z kolesa: „×2" v ľavom hornom rohu (voľný deň nesie rámik celej bunky) */}
    {dbl && <span aria-hidden="true" className="bz-heat-x2">×2</span>}
    {stripeKind ? streakStripe(stripeKind, v) : null}
  </>);
  const cls = "bz-heat-cell" + (free ? " bz-heat-cell--free" : "");
  // bez onSelect = čisto dekoratívna bunka (span) — widget je sám <button>, vnorené buttony sú nevalidné
  if (!d || !onSelect) return <span className={cls} style={style}>{inner}</span>;
  const prizeSay = (free ? ", voľný deň" : "") + (dbl ? ", dvojnásobné body" : "");
  return (
    <button type="button" onClick={() => onSelect(d.day)} className={cls}
      aria-label={(lite ? `${d.day}: ${d.pct || 0} % úspešnosti` : `${d.day}: ${d.done}/${d.due} návykov, ${d.points} b.`) + prizeSay} style={style}>
      {inner}
    </button>
  );
}

function WeekdayHead({ gutter = 0 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: (gutter ? `${gutter}px ` : "") + "repeat(7, 1fr)", gap: 5, marginBottom: 5 }}>
      {gutter ? <span /> : null}
      {WEEKDAYS_SK.map((w) => (
        <span key={w} style={{ textAlign: "center", fontSize: 10.5, fontWeight: 700, color: "var(--muted)", fontFamily: "var(--font-mono)" }}>{w}</span>
      ))}
    </div>
  );
}

// v29 — legenda pod mriežkou: jednotný riadok (mono 11, muted) pre farebnú škálu aj guličky
const LEG_ROW = { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 12px", fontSize: 11, fontFamily: "var(--font-ui)", color: "var(--muted)" };
const LEG_ITEM = { display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" };

// farebná škála intenzity buniek (menej → viac; rovnaká pre Body aj Úspešnosť)
function ColorLegend() {
  return (
    <div style={LEG_ROW}>
      <span>Menej</span>
      <span style={{ display: "inline-flex", gap: 3 }}>
        {[0, 25, 50, 75, 100].map((v) => (
          <span key={v} style={{ width: 13, height: 13, borderRadius: "var(--radius-sm)", background: cellBg(v) }} />
        ))}
      </span>
      <span>Viac</span>
    </div>
  );
}

// vysvetlivky pásikov sérií (aktuálna = plný, najdlhšia = kratší svetlejší)
function StreakLegend() {
  return (
    <div style={LEG_ROW}>
      <span style={LEG_ITEM}>
        <span style={{ width: 14, height: 2.5, borderRadius: "var(--radius-pill)", background: "var(--brand-primary)", flexShrink: 0 }} /> aktuálna séria
      </span>
      <span style={LEG_ITEM}>
        <span style={{ width: 9, height: 2.5, borderRadius: "var(--radius-pill)", background: "var(--brand-primary)", opacity: 0.5, flexShrink: 0 }} /> najdlhšia séria
      </span>
      {/* v32.16 — krúžok v rohu štvorčeka = perfektný deň */}
      <span style={LEG_ITEM}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", boxSizing: "border-box", border: "1.6px solid var(--brand-primary)", flexShrink: 0 }} /> perfektný deň
      </span>
    </div>
  );
}

// v37 — vysvetlivky cien z kolesa výhry. Zobrazuje sa LEN tomu, kto nejakú cenu naozaj
// má (väčšina ľudí nikdy nevyhrá) — inak by legenda zbytočne rástla o mŕtve značky.
function PrizeLegend({ free, dbl }) {
  return (
    <div style={LEG_ROW}>
      {free && <span style={LEG_ITEM}><span className="bz-heat-mark-free" /> voľný deň</span>}
      {dbl && <span style={LEG_ITEM}><span className="bz-heat-mark-x2">×2</span> dvojnásobné body</span>}
    </div>
  );
}

// v32.15 — „Aktivita" žije priamo v Prehľade (bez ďalšieho modalu): hore legenda + detail
// vybraného dňa, dole CELÁ heat mapa vo vlastnom scrolli s focusom na aktuálny týždeň
// (scroll začína na spodku, dá sa scrollovať hore do histórie).
export function HeatmapInline({ streak, streakActive, best, bestEnd, moods = {}, lite = false, perfectDays = 0,
  freeDays = [], doubleDays = [], onEditEntry, entriesKey = 0 }) {
  const [days, setDays] = useState(null);
  const [sel, setSel] = useState(todayKey());
  useEffect(() => {
    let live = true;
    loadActivity(52).then((d) => { if (live) setDays(d); }).catch(() => { if (live) setDays([]); });
    return () => { live = false; };
  }, []);
  const weeks = useMemo(() => {
    const all = toWeeks(days || []);
    let start = 0;
    while (start < all.length - 8 && all[start].every(isEmptyDay)) start++;
    return all.slice(start);
  }, [days]);
  // v33b — prázdny stav: dáta načítané, ale ani jeden deň nemá aktivitu (žiadny due/done/bod).
  // Zrkadlí vzor odznakov pred prvým získaným — informatívna hláška namiesto prázdnej mriežky.
  const hasActivity = useMemo(() => (days || []).some((d) => !isEmptyDay(d)), [days]);
  const ranges = useMemo(() => streakRanges({ streak, streakActive, best, bestEnd }), [streak, streakActive, best, bestEnd]);
  // v37 — ceny z kolesa výhry. Dvojnásobné body sú bodová cena, takže v Lite (bez bodov)
  // nemajú čo zobrazovať; voľný deň chráni sériu, ten platí pre všetkých.
  const freeSet = useMemo(() => new Set(freeDays || []), [freeDays]);
  const dblSet = useMemo(() => new Set(lite ? [] : (doubleDays || [])), [doubleDays, lite]);
  // naplánovaná cena na BUDÚCI deň sa v mriežke objaviť nemôže (tá končí dneškom), preto ju
  // pomenujeme slovom a dátumom nad mriežkou
  const upcoming = useMemo(() => {
    const t = todayKey();
    return [
      ...[...freeSet].filter((k) => k > t).map((k) => ({ day: k, kind: "free" })),
      ...[...dblSet].filter((k) => k > t).map((k) => ({ day: k, kind: "dbl" })),
    ].sort((a, b) => (a.day < b.day ? -1 : 1)).slice(0, 3);
  }, [freeSet, dblSet]);
  const maxPts = useMemo(() => Math.max(1, ...(days || []).map((d) => d.points)), [days]);
  const selDay = (days || []).find((d) => d.day === sel);
  const selMood = moods[sel];
  // DENNÍK §10 — zápisy vybraného dňa sa dotiahnu až po klepnutí (do /api/state nepatria)
  const selJournal = useDayEntries(sel, entriesKey);
  const selEntries = (selJournal && selJournal.day === sel) ? selJournal.entries : [];
  const selDone = (selJournal && selJournal.day === sel) ? (selJournal.done || []) : [];   // splnené návyky dňa (konkrétne)
  const dniSk = (n) => (n === 1 ? "deň" : n >= 2 && n <= 4 ? "dni" : "dní");
  const selStreak = inRange(sel, ranges.cur)
    ? { kind: "cur", label: "Aktuálna séria", len: streak }
    : (inRange(sel, ranges.best) ? { kind: "best", label: "Najdlhšia séria", len: best } : null);
  const monthLabel = (w, i) => {
    const firstOf = w.find((d) => d && new Date(d.day + "T00:00:00").getDate() === 1);
    const src = firstOf || (i === 0 ? w.find(Boolean) : null);
    return src ? new Date(src.day + "T00:00:00").toLocaleDateString("sk-SK", { month: "short" }).replace(".", "") : "";
  };
  const fmtSel = (k) => {
    const d = new Date(k + "T00:00:00");
    const wd = d.toLocaleDateString("sk-SK", { weekday: "short" });
    return wd.charAt(0).toUpperCase() + wd.slice(1) + " " + d.getDate() + ". " + (d.getMonth() + 1) + ".";
  };
  // focus na aktuálny týždeň: scroll mriežky začína na spodku
  const gridRef = useRef(null);
  useEffect(() => {
    const el = gridRef.current;
    if (el && days) el.scrollTop = el.scrollHeight;
  }, [days]);
  return (
    <div data-testid="heatmap-widget" style={{ marginTop: 12, background: "var(--surface-solid)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: 14 }}>
      {/* N1 — krátke vysvetlenie, čo heat mapa ukazuje (Lite aj non-Lite) */}
      <div style={{ fontSize: 12.5, color: "var(--neutral-text-muted)", lineHeight: 1.5, marginBottom: 12 }}>
        {lite
          ? "Každý štvorček je jeden deň — čím tmavší, tým väčšia časť návykov splnená. Ťukni na deň pre detail."
          : "Každý štvorček je jeden deň — čím tmavší, tým viac bodov si v ten deň získala. Ťukni na deň pre detail."}
      </div>
      {/* v33h — Lite súhrn (séria/perfektné dni) v hlavičke odstránený; značky sú v mriežke */}
      {/* v37 — cena z kolesa naplánovaná na budúci deň: mriežka končí dneškom, takže na ňu
          upozorníme slovom (inak by človek nevedel, že ho niečo čaká) */}
      {upcoming.length > 0 && (
        <div className="bz-heat-upcoming">
          <span>Čaká ťa:</span>
          {upcoming.map((u) => (
            <span key={u.kind + u.day} className="bz-heat-upcoming-item">
              {u.kind === "free"
                ? <><span className="bz-heat-mark-free" /> voľný deň</>
                : <><span className="bz-heat-mark-x2">×2</span> dvojnásobné body</>}
              {" · "}<b style={{ color: "var(--text)" }}>{fmtSel(u.day)}</b>
            </span>
          ))}
        </div>
      )}
      {/* v37 — detail VYBRANÉHO dňa: jediná časť widgetu, ktorá sa mení ťuknutím do mriežky.
          Predtým visel voľne medzi vysvetlivkou a mriežkou (delila ho len linka), takže sa dal
          prečítať ako ďalší kus statického textu. Teraz má vlastnú plochu a nadpis. */}
      {hasActivity && selDay && (
        <div className="bz-heat-sel">
          <span className="bz-sectlabel bz-heat-sel__label">Vybraný deň</span>
          <div style={{ display: "flex", alignItems: "baseline", gap: 9, minWidth: 0 }}>
            <span style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, color: "var(--text)", whiteSpace: "nowrap" }}>
              {lite ? `${selDay.pct} %` : `${selDay.points} b.`}
            </span>
            <span style={{ fontSize: 13, color: "var(--neutral-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {fmtSel(sel)}{selDay.due > 0 ? (lite ? ` · ${selDay.done}/${selDay.due} návykov` : ` · ${selDay.done}/${selDay.due} návykov · ${selDay.pct} %`) : " · bez naplánovaných návykov"}
            </span>
          </div>
          {selStreak && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--neutral-text-muted)" }}>
              {selStreak.kind === "cur"
                ? <span style={{ width: 5, height: 5, borderRadius: "var(--radius-pill)", background: "var(--brand-primary)", flexShrink: 0 }} />
                : <span style={{ width: 6, height: 6, borderRadius: "var(--radius-pill)", border: "1.5px solid var(--brand-primary)", flexShrink: 0 }} />}
              <span>{selStreak.label} · <b style={{ color: "var(--text)" }}>{selStreak.len} {dniSk(selStreak.len)}</b></span>
            </div>
          )}
          {/* v37 — ceny z kolesa pomenované slovom (značka v mriežke sa dá zabudnúť) */}
          {freeSet.has(sel) && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--neutral-text-muted)" }}>
              <span className="bz-heat-mark-free" />
              <span><b style={{ color: "var(--text)" }}>Voľný deň</b> · ráta sa do série aj ako perfektný deň</span>
            </div>
          )}
          {dblSet.has(sel) && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--neutral-text-muted)" }}>
              <span className="bz-heat-mark-x2">×2</span>
              <span><b style={{ color: "var(--text)" }}>Dvojnásobné body</b>{selDay.points > 0 ? ` · +${selDay.points} b navyše` : " · v tento deň si zatiaľ nič nesplnil/a"}</span>
            </div>
          )}
          {selMood && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 7, minWidth: 0 }}>
              <span style={{ display: "inline-flex", flexShrink: 0 }}><MoodFace k={moodIconName(selMood)} size={18} /></span>
              <span style={{ fontSize: 12.5, color: "var(--neutral-text-muted)", lineHeight: 1.45 }}>
                {MOODS.find((m) => m.id === selMood)?.label || ""}
              </span>
            </div>
          )}
          {/* Návyky dňa. Farbu nesie IKONA návyku (`icon_color`, ako všade inde v appke) —
              nie chip: jedna zelená pre všetko by tvrdila, že „splnené" je vlastnosť
              chipu, a v 24 témach by trčala. Merateľný návyk ukazuje HODNOTU (číslo je
              to hlavné, čo v ten deň vzniklo), rozbehnutý pokus („N× za obdobie" nesie
              cieľ cez polnoc) je stlmený, aby sa nečítal ako splnenie. */}
          {selDone.length > 0 && (
            <div className="bz-heat-done" aria-label="Návyky dňa">
              {selDone.map((h) => (
                <span key={h.id}
                      className={`bz-heat-done__chip${h.done ? "" : " bz-heat-done__chip--partial"}`}
                      title={h.done
                        ? (h.by_entries ? "Splnené zápismi do denníka" : "Splnené")
                        : "Zapísané — cieľ zatiaľ nie je dosiahnutý"}>
                  <span className="bz-heat-done__icon" style={iconChipStyle(h.icon_color)}>
                    <Glyph icon={h.icon} size={13} />
                  </span>
                  <span className="bz-heat-done__label">{h.label}</span>
                  {h.amount > 0 && (
                    <b className="bz-heat-done__val">{fmtMetric(h.amount, h.unit, h.is_time)}</b>
                  )}
                  {h.by_entries && <span className="bz-heat-done__from">zo zápisov</span>}
                </span>
              ))}
            </div>
          )}
          {selEntries.length > 0 && (
            <div className="bz-journal__list">
              {selEntries.map((e) => <EntryRow key={e.id} e={e} onClick={() => onEditEntry && onEditEntry(e)} />)}
            </div>
          )}
        </div>
      )}
      {days === null ? (
        <div style={{ fontSize: 13, color: "var(--muted)", padding: "14px 0" }}>Načítavam…</div>
      ) : !hasActivity ? (
        <EmptyState icon="calendar" title="Zatiaľ žiadna aktivita"
          desc="Splň dnes svoj prvý návyk a tu ti pribudne prvý farebný štvorček — mapa sa časom sama zaplní." />
      ) : (<>
        <WeekdayHead gutter={30} />
        <div ref={gridRef} data-testid="heatmap-grid" style={{ maxHeight: "46vh", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          {weeks.map((w, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "30px repeat(7, 1fr)", gap: 5, marginBottom: 5 }}>
              <span style={{ fontSize: 10.5, color: "var(--muted)", fontFamily: "var(--font-mono)", alignSelf: "center" }}>{monthLabel(w, i)}</span>
              {w.map((d, j) => <DayCell key={j} d={d} maxPts={maxPts} selected={d && d.day === sel} onSelect={setSel}
                ranges={ranges} mood={d ? moods[d.day] : null} lite={lite}
                free={!!d && freeSet.has(d.day)} dbl={!!d && dblSet.has(d.day)} />)}
            </div>
          ))}
        </div>
      </>)}
      {/* legenda ako sticky pätička na spodku — počas načítavania aj pri aktivite; v prázdnom
          stave (žiadna aktivita) sa skryje, nech nemätie prázdnou škálou */}
      {(days === null || hasActivity) && (
        <div style={{ position: "sticky", bottom: 0, zIndex: 1, marginTop: 12, marginLeft: -14, marginRight: -14, marginBottom: -14, padding: "10px 14px", background: "var(--surface-solid)", borderTop: "1px solid var(--border)", borderRadius: "0 0 var(--radius-lg) var(--radius-lg)", display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "8px 12px" }}>
          <ColorLegend />
          {/* v33h — séria aj perfektný deň sa značia aj v Lite → legenda platí pre všetkých */}
          <StreakLegend />
          {/* v37 — značky cien z kolesa vysvetľujeme len tomu, kto nejakú cenu má */}
          {(freeSet.size > 0 || dblSet.size > 0) && <PrizeLegend free={freeSet.size > 0} dbl={dblSet.size > 0} />}
        </div>
      )}
    </div>
  );
}
